/**
 * Севооборот (вкладка «Севооборот», эталон founder v3 2026-07-11) — чистая
 * логика маппинга leaf-раскладки в UX-модель «Куда сеем / Итог по полю».
 *
 * Термины пользователя: «посев / посеять / под паром». Никаких «leaf/XOR/API»
 * в подписях — это внутренняя модель ADR-049, на экран не протекает.
 */
import { fieldStateLabel } from './constants';
import type { CodeLabeler } from '@/lib/reference/fieldCatalogs';
import type { CropHistoryRow, OccupancyLeaf, OccupancyView } from './types';

/** Есть ли у поля отдельные зоны (круг/углы/чек), между которыми выбираем цель. */
export function fieldHasZones(view: OccupancyView | undefined): boolean {
  if (!view) return false;
  return view.leaves.some((l) => l.subzone_id != null);
}

/**
 * Подпись посева одного участка для «Итога по полю» / истории.
 *
 * `fallowLabel` — лукап состояния поля из справочника (`useFieldStateLabel()`).
 * Пока справочник грузится — общее «Под паром», код на экран не течёт.
 */
export function leafSowingLabel(
  leaf: OccupancyLeaf,
  fallowLabel?: CodeLabeler,
): string | null {
  const s = leaf.sowing;
  if (!s) return null;
  // Пар/АМП — занятие без культуры; подпись даёт справочник состояний.
  if (s.occupancy_type === 'fallow') {
    return fieldStateLabel(s.fallow_kind, fallowLabel);
  }
  return s.crop_name ?? 'Культура';
}

/**
 * AK-446 — заголовок блока действий и диалога подтверждения.
 *
 * Агроном должен видеть, ЧТО именно он сеет/убирает, до нажатия и в модалке —
 * на поле из четырёх чеков «Посеять» без имени участка это гарантированная
 * ошибка не в ту зону (второй рубеж после кнопки в строке зоны).
 * Формат: «Пшеница озимая · Круг (орошение)» / «Пар чёрный · Всё поле» /
 * «Всё поле» — когда посева нет и называть нечего, кроме места.
 */
export function sowingActionsTitle(
  leaf: OccupancyLeaf,
  fallowLabel?: CodeLabeler,
): string {
  const what = leafSowingLabel(leaf, fallowLabel);
  const where = leaf.name?.trim() || 'Всё поле';
  return what ? `${what} · ${where}` : where;
}

export interface FieldSowSummaryItem {
  label: string;
  areaHa: number | null;
  type: 'crop' | 'fallow';
}

export interface FieldSowSummary {
  /** Сколько участков реально засеяно (культура ИЛИ пар). */
  sownCount: number;
  /** Всего участков (leaf'ов) на поле. */
  leafCount: number;
  /** Суммарная площадь всех участков поля, га (для «· 60,8 га»). */
  totalAreaHa: number | null;
  items: FieldSowSummaryItem[];
}

/**
 * Участки поля, которые показываем как посевные единицы (карточки посева,
 * итог по полю, попап карты).
 *
 * Ответ API содержит и участок «всё поле», и зоны (AK-449) — это нужно вкладке
 * «Посевы», чтобы предложить выбор «Куда сеем». Но остальным экранам поле
 * сеется ЛИБО целиком, ЛИБО по зонам, поэтому:
 *   · засеяны зоны              → показываем зоны (поле целиком — контейнер);
 *   · засеяно поле целиком      → показываем поле (зоны в этом сезоне пустые);
 *   · ничего не засеяно         → зоны, если заведены, иначе поле.
 * Иначе рядом с зонными карточками висело бы «Всё поле · 259,2 га — Не
 * посеяно», и площадь читалась бы как посчитанная дважды.
 */
export function sownUnitLeaves(leaves: OccupancyLeaf[]): OccupancyLeaf[] {
  const zones = leaves.filter((l) => l.subzone_id != null);
  if (!zones.length) return leaves;
  const whole = leaves.filter((l) => l.subzone_id == null);
  // Посев на поле целиком старше по приоритету: по правилу «целиком ⊕ по
  // зонам» вместе они существовать не могут, а если легаси-данные всё же
  // содержат оба — показываем поле, чтобы площадь не сложилась дважды.
  if (whole.some((w) => w.sowing)) return whole;
  return zones;
}

/**
 * Итог по полю — что и на какой площади посеяно суммарно. Считается из
 * leaf-раскладки автоматически (эталон: «2 посева · кукуруза 40 га +
 * подсолнечник 20,8 га · 60,8 га»).
 *
 * И посевы, и площадь берём из ОДНОГО набора участков (`sownUnitLeaves`):
 * иначе на поле-поливе, засеянном целиком, культура шла бы от поля, а площадь
 * — суммой кругов и богары (у overlay они не покрывают поле целиком) и цифры
 * в одной строке разошлись бы.
 */
export function summarizeField(
  view: OccupancyView | undefined,
  fallowLabel?: CodeLabeler,
): FieldSowSummary {
  const units = sownUnitLeaves(view?.leaves ?? []);
  const items: FieldSowSummaryItem[] = [];
  let totalAreaHa: number | null = null;
  for (const leaf of units) {
    if (leaf.area_ha != null) totalAreaHa = (totalAreaHa ?? 0) + leaf.area_ha;
    const label = leafSowingLabel(leaf, fallowLabel);
    if (label && leaf.sowing) {
      items.push({
        label,
        areaHa: leaf.area_ha,
        type: leaf.sowing.occupancy_type,
      });
    }
  }
  return {
    sownCount: items.length,
    leafCount: units.length,
    totalAreaHa,
    items,
  };
}

/**
 * Список целей «Куда сеем»: сначала «Всё поле», затем зоны поля.
 *
 * Берём то, что реально прислал сервер: участок поля целиком приходит вместе
 * с зонами (AK-449), поэтому его площадь — настоящая площадь поля, а не сумма
 * зон (у поля-полива круги + богара могут не покрывать поле целиком).
 * Старый ответ (только зоны, без участка поля) переживаем: синтезируем «Всё
 * поле» из суммы зон.
 */
export function buildSowTargets(
  view: OccupancyView | undefined,
): OccupancyLeaf[] {
  if (!view) return [];
  const zones = view.leaves.filter((l) => l.subzone_id != null);
  if (!zones.length) return view.leaves;
  const whole = view.leaves.find((l) => l.subzone_id == null);
  const wholeLeaf: OccupancyLeaf = {
    subzone_id: null,
    name: 'Всё поле',
    subzone_kind: null,
    overlap_mode: null,
    area_ha: whole?.area_ha ?? summarizeField(view).totalAreaHa,
    sowing: whole?.sowing ?? null,
  };
  return [wholeLeaf, ...zones];
}

/** Почему сейчас нельзя посеять выбранный участок + куда идти, чтобы можно было. */
export interface SowBlock {
  /** Объяснение на языке агронома (без «leaf/XOR/API»). */
  reason: string;
  /** Участок, где лежит мешающий посев — туда переключаем пользователя. */
  gotoSubzoneId: string | null;
  /** Подпись кнопки перехода. */
  gotoLabel: string;
}

/**
 * Общая середина обоих отказов. Правило одно — объясняем его одной фразой и
 * слово в слово как backend (`services/sowing_lifecycle.py`, AK-549): человек
 * не должен читать два разных текста про одно и то же в зависимости от того,
 * успел ли экран посчитать раскладку раньше сервера.
 */
const WHY_XOR =
  'Поле сеется либо целиком, либо по зонам — иначе площадь посчитается дважды.';

/**
 * Поле сеется ЛИБО целиком, ЛИБО по зонам (иначе площадь считается дважды —
 * это же правило держит база и отвергает запись). Здесь мы говорим об этом
 * ЗАРАНЕЕ и человеческим языком, а не показываем ошибку после сохранения.
 */
export function sowBlock(
  view: OccupancyView | undefined,
  targetSubzoneId: string | null,
): SowBlock | null {
  if (!view || !fieldHasZones(view)) return null;
  const zones = view.leaves.filter((l) => l.subzone_id != null);
  const whole = view.leaves.find((l) => l.subzone_id == null);

  if (isWholeFieldTarget(targetSubzoneId)) {
    const sown = zones.filter((z) => z.sowing);
    if (!sown.length) return null;
    const names = sown
      .map((z) => `${z.name} (${(leafSowingLabel(z) ?? 'посев').toLowerCase()})`)
      .join(', ');
    return {
      reason:
        `Заняты зоны: ${names}. ${WHY_XOR} ` +
        'Чтобы посеять поле целиком, сначала снимите посевы зон.',
      gotoSubzoneId: sown[0].subzone_id,
      gotoLabel: `Перейти к «${sown[0].name}»`,
    };
  }

  if (!whole?.sowing) return null;
  // Чем и на какой площади занято — ровно то, что называет 409 сервера. Без
  // площади агроном не понимает, что именно придётся снимать.
  const what = leafSowingLabel(whole) ?? 'посев';
  const area = whole.area_ha != null ? `, ${fmtHa(whole.area_ha)}` : '';
  return {
    reason:
      `Поле занято целиком: ${what}${area}. ${WHY_XOR} ` +
      'Чтобы сеять по зонам, сначала снимите посев со всего поля.',
    gotoSubzoneId: null,
    gotoLabel: 'Перейти к «Всё поле»',
  };
}

/**
 * Сводка «сколько зон занято и на какой площади» — её показывает участок «Всё
 * поле» ВМЕСТО кнопки посева целиком (AK-549). Пустое место на месте кнопки
 * читается как «сломалось»; цифра сразу отвечает, куда ушла площадь поля.
 */
export function occupiedZonesSummary(
  view: OccupancyView | undefined,
): { count: number; areaHa: number | null } | null {
  if (!view) return null;
  const sown = view.leaves.filter((l) => l.subzone_id != null && l.sowing);
  if (!sown.length) return null;
  let areaHa: number | null = null;
  for (const z of sown) {
    if (z.area_ha != null) areaHa = (areaHa ?? 0) + z.area_ha;
  }
  return { count: sown.length, areaHa };
}

/** «1 зона» / «2 зоны» / «5 зон» — счётная форма для сводки выше. */
export function zonesWord(count: number): string {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'зон';
  if (mod10 === 1) return 'зона';
  if (mod10 >= 2 && mod10 <= 4) return 'зоны';
  return 'зон';
}

/** Целевой участок = «всё поле» (default-leaf, subzone_id === null). */
export function isWholeFieldTarget(subzoneId: string | null): boolean {
  return subzoneId == null;
}

/** Подпись кнопки посева: «Посеять всё поле» vs «Посеять зону». */
export function sowButtonLabel(subzoneId: string | null): string {
  return isWholeFieldTarget(subzoneId)
    ? 'Посеять всё поле'
    : 'Посеять зону';
}

/**
 * Найти прошлогодний посев для того же участка — для «↩︎ Повторить прошлый
 * сезон». Берём самый свежий сезон СТРОГО раньше текущего с культурой (пар не
 * повторяем — сеять пар «повтором» смысла нет). Матч по subzone_id (зона к
 * зоне, всё-поле к всему-полю).
 */
export function findRepeatCandidate(
  history: CropHistoryRow[] | undefined,
  targetSubzoneId: string | null,
  currentSeasonYear: number | null | undefined,
): CropHistoryRow | null {
  if (!history?.length) return null;
  const prior = history
    .filter((r) => r.occupancy_type === 'crop' && r.crop_id)
    .filter((r) => (r.subzone_id ?? null) === (targetSubzoneId ?? null))
    .filter(
      (r) =>
        currentSeasonYear == null ||
        r.season_year == null ||
        r.season_year < currentSeasonYear,
    );
  // history уже отсортирована свежий-сверху → первый и есть самый недавний.
  return prior[0] ?? null;
}

/** т/га-эталон подписи урожайности: число в ц/га или «—». */
export function fmtYieldCHa(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
}

/** Площадь, га — «60,8 га» / «—». */
export function fmtHa(v: number | null | undefined): string {
  return v != null
    ? `${v.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} га`
    : '—';
}
